import { VariantUpdateEvent } from '@theme/events';

/** @param {object} variant @param {number} ix */
function optionAt(variant, ix) {
  const keys = /** @type {const} */ (['option1', 'option2', 'option3']);
  return String(variant[keys[ix]] ?? '').trim();
}

/** @param {string} text */
function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * @typedef {object} ConfiguratorConfig
 * @property {string} productId
 * @property {string} productHandle
 * @property {'radiator' | 'valve' | 'standard'} mode
 * @property {'cast-iron' | 'standard'} layout
 * @property {boolean} showSizeSelector
 * @property {boolean} deferSizeCards
 * @property {number} finishOptionIndex
 * @property {number | null} heightOptionIndex
 * @property {number | null} widthOptionIndex
 * @property {number | null} sizeOptionIndex
 * @property {number[]} extraOptionIndices
 */

class ProductConfigurator extends HTMLElement {
  /** @type {ConfiguratorConfig} */
  #config;

  /** @type {Array<object>} */
  #variants;

  /** @type {boolean} */
  #panelClickBound = false;

  async connectedCallback() {
    const cfgEl = this.querySelector('[data-configurator-config]');
    const varEl = this.querySelector('[data-configurator-variants]');
    if (!cfgEl || !varEl) return;

    try {
      this.#config = JSON.parse(cfgEl.textContent || '{}');
      this.#variants = JSON.parse(varEl.textContent || '[]');
    } catch (e) {
      console.error('[product-configurator] Invalid JSON', e);
      return;
    }

    const sizeSection = this.querySelector('[data-size-section]');
    const domFinishIx = sizeSection?.getAttribute('data-finish-option-index');
    if (domFinishIx != null && domFinishIx !== '') {
      const parsed = parseInt(domFinishIx, 10);
      if (!Number.isNaN(parsed)) this.#config.finishOptionIndex = parsed;
    } else {
      let n = Number(this.#config.finishOptionIndex);
      if (!Number.isFinite(n)) n = 0;
      this.#config.finishOptionIndex = n;
    }

    if (sizeSection?.getAttribute('data-cast-iron-layout') === 'true') {
      this.#config.layout = 'cast-iron';
    }
    const domHeightIx = sizeSection?.getAttribute('data-height-option-index');
    if (domHeightIx != null && domHeightIx !== '') {
      const parsed = parseInt(domHeightIx, 10);
      if (!Number.isNaN(parsed)) this.#config.heightOptionIndex = parsed;
    } else if (this.#config.heightOptionIndex != null && this.#config.heightOptionIndex !== '') {
      this.#config.heightOptionIndex = Number(this.#config.heightOptionIndex);
    }
    const domWidthIx = sizeSection?.getAttribute('data-width-option-index');
    if (domWidthIx != null && domWidthIx !== '') {
      const parsed = parseInt(domWidthIx, 10);
      if (!Number.isNaN(parsed)) this.#config.widthOptionIndex = parsed;
    } else if (this.#config.widthOptionIndex != null && this.#config.widthOptionIndex !== '') {
      this.#config.widthOptionIndex = Number(this.#config.widthOptionIndex);
    }

    if (this.#config.sizeOptionIndex != null && this.#config.sizeOptionIndex !== '') {
      this.#config.sizeOptionIndex = Number(this.#config.sizeOptionIndex);
    }
    this.#config.extraOptionIndices = Array.isArray(this.#config.extraOptionIndices)
      ? this.#config.extraOptionIndices.map((ix) => Number(ix)).filter((ix) => Number.isFinite(ix))
      : [];

    if (this.#config.deferSizeCards && this.#config.showSizeSelector) {
      this.#buildSizeCardsFromVariants();
    }

    try {
      await this.#loadAllVariants();
      if (this.#config.deferSizeCards && this.#config.showSizeSelector) {
        this.#buildSizeCardsFromVariants();
      }
    } catch (e) {
      console.error('[product-configurator] Failed to load variant pages', e);
      if (this.#config.deferSizeCards && this.#config.showSizeSelector) {
        const list = this.querySelector('[data-size-list]');
        if (list && !list.querySelector('[data-size-card]')) {
          this.#buildSizeCardsFromVariants();
        }
      }
    }

    this.#bindPanelClicks();
    this.#bindGalleryThumbs();
    this.#bindGalleryNav();
    this.#hideDuplicateThemeVariantUi();
    requestAnimationFrame(() => this.#hideDuplicateThemeVariantUi());
    setTimeout(() => this.#hideDuplicateThemeVariantUi(), 300);

    const initialVid = this.#getInitialVariantId();
    this.#syncUiFromVariantId(initialVid);
    requestAnimationFrame(() => this.#syncUiFromVariantId(this.#getInitialVariantId()));
    setTimeout(() => this.#syncUiFromVariantId(this.#getInitialVariantId()), 50);
  }

  async #loadAllVariants() {
    const metaEl = this.querySelector('[data-configurator-variant-meta]');
    if (!metaEl) return;

    const meta = JSON.parse(metaEl.textContent || '{}');
    const variantsCount = Number(meta.variantsCount) || this.#variants.length;
    const variantPages = Number(meta.variantPages) || 1;
    const pageParam = meta.pageParam || 'page';
    const sectionId = meta.variantsSection || 'rfhp-configurator-variants';
    const handle = this.#config.productHandle;

    if (!handle || variantPages <= 1 || this.#variants.length >= variantsCount) return;

    const root = window.Shopify?.routes?.root || '/';
    const baseUrl = `${root}products/${encodeURIComponent(handle)}`;

    /** @type {Map<number, object>} */
    const byId = new Map(this.#variants.map((v) => [Number(v.id), v]));

    for (let page = 2; page <= variantPages; page += 1) {
      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('sections', sectionId);
      url.searchParams.set(pageParam, String(page));

      const res = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`Variant page ${page} failed (${res.status})`);

      const data = await res.json();
      const html = data[sectionId];
      if (!html) continue;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const chunkEl = doc.querySelector('[data-rfhp-variant-chunk]');
      if (!chunkEl?.textContent) continue;

      const chunk = JSON.parse(chunkEl.textContent);
      if (!Array.isArray(chunk)) continue;

      chunk.forEach((v) => {
        if (v?.id != null) byId.set(Number(v.id), v);
      });
    }

    this.#variants = [...byId.values()];
  }

  /** @returns {boolean} */
  #isCastIronLayout() {
    return this.#config.layout === 'cast-iron';
  }

  #buildSizeCardsFromVariants() {
    const list = this.querySelector('[data-size-list]');
    if (!list) return;

    const finishIx = this.#config.finishOptionIndex;
    const heightIx = this.#config.heightOptionIndex;
    const widthIx = this.#config.widthOptionIndex;
    const castIron = this.#isCastIronLayout();
    const selectedId = this.#getInitialVariantId();
    const selectedVariant =
      this.#findVariant(selectedId) || this.#variants.find((v) => v) || null;
    const selectedFinish = selectedVariant ? optionAt(selectedVariant, finishIx) : '';
    const selectedHeight =
      castIron && typeof heightIx === 'number' && selectedVariant
        ? optionAt(selectedVariant, heightIx)
        : '';

    const seen = new Set();
    const sorted = [...this.#variants].sort((a, b) => Number(a.price) - Number(b.price));
    const fragment = document.createDocumentFragment();

    for (const variant of sorted) {
      const o1 = optionAt(variant, 0);
      const o2 = optionAt(variant, 1);
      const o3 = optionAt(variant, 2);
      const cardFinish = optionAt(variant, finishIx);
      const cardHeight = typeof heightIx === 'number' ? optionAt(variant, heightIx) : '';
      const cardWidth = typeof widthIx === 'number' ? optionAt(variant, widthIx) : '';

      const w = variant.width;
      const h = variant.height;
      let dedupeToken;
      if (castIron) {
        dedupeToken = `${cardFinish}::h::${cardHeight}::w::${cardWidth}`;
      } else {
        const sigParts = [];
        if (finishIx !== 0 && o1) sigParts.push(o1);
        if (finishIx !== 1 && o2) sigParts.push(o2);
        if (finishIx !== 2 && o3) sigParts.push(o3);
        const sizeSignature = sigParts.join(' · ');
        if (sizeSignature) {
          dedupeToken = `${cardFinish}::sig::${sizeSignature}`;
        } else if (w != null && h != null) {
          dedupeToken = `${cardFinish}::wh::${w}x${h}`;
        } else {
          dedupeToken = `${cardFinish}::id::${variant.id}`;
        }
      }

      if (seen.has(dedupeToken)) continue;
      seen.add(dedupeToken);

      const heatW = variant.heat_watts_t50;
      const heatBtu = variant.heat_btus_t50;
      const heatWDisplay = heatW != null ? Math.round(Number(heatW) * 0.51) : null;
      const heatBtuDisplay = heatBtu != null ? Math.round(Number(heatBtu) * 0.51) : null;
      const depth = variant.depth;
      const delivery = variant.delivery;

      const isSelected = Number(variant.id) === Number(selectedVariant?.id);

      let title;
      if (w != null && h != null) {
        title = `${h}mm (H) × ${w}mm (W)`;
      } else if (!castIron) {
        const sigParts = [];
        if (finishIx !== 0 && o1) sigParts.push(o1);
        if (finishIx !== 1 && o2) sigParts.push(o2);
        if (finishIx !== 2 && o3) sigParts.push(o3);
        const sizeSignature = sigParts.join(' · ');
        title = sizeSignature || variant.title || 'Size';
      } else if (cardWidth && cardHeight) {
        title = `${cardHeight}mm (H) × ${cardWidth}mm (W)`;
      } else {
        title = variant.title || 'Size';
      }

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `configurator-size-card${isSelected ? ' is-selected' : ''}`;
      btn.dataset.sizeCard = '';
      btn.dataset.variantId = String(variant.id);
      btn.dataset.finishValue = cardFinish;
      btn.dataset.heightValue = cardHeight;
      btn.dataset.option0 = o1;
      btn.dataset.option1 = o2;
      btn.dataset.option2 = o3;
      if (heatW != null) btn.dataset.rfhpWattsBase = String(heatW);
      if (heatBtu != null) btn.dataset.rfhpBtuBase = String(heatBtu);
      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

      const attrs = [];
      if (heatW != null) {
        attrs.push(
          `<div class="configurator-size-card__row"><dt>Heat Output (<span data-rfhp-dt-watts>ΔT30</span> Watts)</dt><dd><span data-rfhp-heat-watts>${heatWDisplay}W</span></dd></div>`
        );
      }
      if (heatBtu != null) {
        attrs.push(
          `<div class="configurator-size-card__row"><dt>Heat Output (<span data-rfhp-dt-btu>ΔT30</span> BTUs)</dt><dd><span data-rfhp-heat-btu>${heatBtuDisplay} BTUs</span></dd></div>`
        );
      }
      if (depth != null && depth !== '') {
        attrs.push(
          `<div class="configurator-size-card__row"><dt>Depth</dt><dd>${escapeHtml(depth)}mm</dd></div>`
        );
      }
      if (delivery) {
        attrs.push(
          `<div class="configurator-size-card__row"><dt>Delivery</dt><dd>${escapeHtml(delivery)}</dd></div>`
        );
      }

      btn.innerHTML = `
        <div class="configurator-size-card__body">
          <h4 class="configurator-size-card__title">${escapeHtml(title)}</h4>
          <dl class="configurator-size-card__attrs">${attrs.join('')}</dl>
        </div>
        <div class="configurator-size-card__price">
          <span class="configurator-size-card__price-value" data-card-price>${escapeHtml(variant.price_money || '')}</span>
          <span class="configurator-size-card__price-note">Incl. VAT</span>
          <span class="configurator-size-card__selected-label" data-card-selected-label${isSelected ? '' : ' hidden'}>Selected</span>
        </div>
      `;

      fragment.appendChild(btn);
    }

    if (!fragment.childElementCount) {
      const err = document.createElement('p');
      err.className = 'configurator-size-list__loading';
      err.textContent = 'No sizes available for this configuration.';
      list.replaceChildren(err);
    } else {
      list.replaceChildren(fragment);
    }
    list.removeAttribute('data-rfhp-sizes-loading');

    const initialVid = this.#getInitialVariantId();
    const v = this.#findVariant(initialVid) || this.#variants[0];
    if (v) {
      this.#updateHeightState(v);
      this.#updateSizeCards(v);
    }
  }

  /**
   * Horizon can show Finish/Size again via the variant-picker block or the swatches block.
   * Liquid suppression is unreliable (e.g. product.collections empty in some contexts).
   * Hide only the main product column pickers that match this product id.
   */
  #hideDuplicateThemeVariantUi() {
    if (this.#config.mode !== 'radiator' || !this.#config.showSizeSelector) return;

    const pid = String(this.#config.productId);
    const roots = document.querySelectorAll('.product-information');
    if (!roots.length) return;

    const selectors = ['variant-picker', 'product-swatches', 'swatches-variant-picker-component'];

    roots.forEach((root) => {
      for (const sel of selectors) {
        root.querySelectorAll(sel).forEach((el) => {
          const id = el.getAttribute('data-product-id');
          if (id !== pid) return;

          el.setAttribute('hidden', '');
          el.setAttribute('aria-hidden', 'true');
          el.classList.add('rfhp--hidden-by-configurator');
        });
      }
    });
  }

  /** @returns {number} */
  #getInitialVariantId() {
    const input = this.querySelector('input[name="id"]');
    const v = input?.value;
    return v ? Number(v) : Number(this.#variants[0]?.id);
  }

  /** @param {number} variantId */
  #findVariant(variantId) {
    return this.#variants.find((v) => Number(v.id) === Number(variantId));
  }

  /** @param {Record<number, string>} required @param {object | undefined} current */
  #findBestVariant(required, current) {
    const entries = Object.entries(required).map(([ix, val]) => [Number(ix), String(val).trim()]);
    const candidates = this.#variants.filter((v) => entries.every(([ix, val]) => optionAt(v, ix) === val));
    if (!candidates.length) return null;
    if (!current) return candidates[0];

    let best = candidates[0];
    let bestScore = -1;
    for (const v of candidates) {
      let score = 0;
      for (let ix = 0; ix < 3; ix += 1) {
        if (Object.prototype.hasOwnProperty.call(required, ix)) continue;
        if (optionAt(v, ix) && optionAt(v, ix) === optionAt(current, ix)) score += 1;
      }
      if (v.available) score += 0.1;
      if (score > bestScore) {
        best = v;
        bestScore = score;
      }
    }
    return best;
  }

  /** @param {object | undefined} current @returns {Record<number, string>} */
  #castIronRequiredFromCurrent(current) {
    /** @type {Record<number, string>} */
    const required = {};
    if (!current) return required;
    const heightIx = this.#config.heightOptionIndex;
    const widthIx = this.#config.widthOptionIndex;
    if (typeof heightIx === 'number') required[heightIx] = optionAt(current, heightIx);
    if (typeof widthIx === 'number') required[widthIx] = optionAt(current, widthIx);
    return required;
  }

  #bindPanelClicks() {
    if (this.#panelClickBound) return;
    this.#panelClickBound = true;

    this.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const sizeCard = target.closest('[data-size-card]');
      if (sizeCard && this.contains(sizeCard)) {
        const id = Number(sizeCard.getAttribute('data-variant-id'));
        const v = this.#findVariant(id);
        if (v) this.#applyVariant(v);
        return;
      }

      const heightBtn = target.closest('[data-height-button]');
      if (heightBtn && this.contains(heightBtn)) {
        const heightValue = (heightBtn.getAttribute('data-height-value') || '').trim();
        const repId = Number(heightBtn.getAttribute('data-representative-variant-id'));
        const heightIx = this.#config.heightOptionIndex;
        if (!heightValue || typeof heightIx !== 'number') return;

        const currentId = Number(this.querySelector('input[name="id"]')?.value);
        const current = this.#findVariant(currentId) || this.#variants[0];
        /** @type {Record<number, string>} */
        const required = {
          [this.#config.finishOptionIndex]: current
            ? optionAt(current, this.#config.finishOptionIndex)
            : '',
          [heightIx]: heightValue,
        };
        const widthIx = this.#config.widthOptionIndex;
        if (current && typeof widthIx === 'number') {
          required[widthIx] = optionAt(current, widthIx);
        }

        let next = this.#findBestVariant(required, current);
        if (!next && current && typeof widthIx === 'number') {
          delete required[widthIx];
          next = this.#findBestVariant(required, current);
        }
        if (!next) next = this.#findVariant(repId);
        if (next) this.#applyVariant(next);
        return;
      }

      const finishBtn = target.closest('[data-finish-button]');
      if (finishBtn && this.contains(finishBtn)) {
        const finishValue = (finishBtn.getAttribute('data-finish-value') || '').trim();
        const repId = Number(finishBtn.getAttribute('data-representative-variant-id'));

        if (this.#config.mode === 'valve' || !this.#config.showSizeSelector) {
          const v = this.#variants.find((x) => Number(x.id) === repId) || this.#findVariant(repId);
          if (v) this.#applyVariant(v);
          return;
        }

        const currentId = Number(this.querySelector('input[name="id"]')?.value);
        const current = this.#findVariant(currentId) || this.#variants[0];
        const required = { [this.#config.finishOptionIndex]: finishValue };
        if (current) {
          if (this.#isCastIronLayout()) {
            Object.assign(required, this.#castIronRequiredFromCurrent(current));
          } else {
            const sizeIx = this.#config.sizeOptionIndex;
            if (typeof sizeIx === 'number') required[sizeIx] = optionAt(current, sizeIx);
            this.#config.extraOptionIndices.forEach((ix) => {
              required[ix] = optionAt(current, ix);
            });
          }
        }

        let next = this.#findBestVariant(required, current);
        if (!next && current) {
          if (this.#isCastIronLayout() && typeof this.#config.widthOptionIndex === 'number') {
            delete required[this.#config.widthOptionIndex];
            next = this.#findBestVariant(required, current);
          } else if (typeof this.#config.sizeOptionIndex === 'number') {
            delete required[this.#config.sizeOptionIndex];
            next = this.#findBestVariant(required, current);
          }
        }
        if (!next) next = this.#findVariant(repId);
        if (next) this.#applyVariant(next);
        return;
      }

      const extraBtn = target.closest('[data-extra-option-button]');
      if (extraBtn && this.contains(extraBtn)) {
        const targetIx = Number(extraBtn.getAttribute('data-option-index'));
        const targetVal = (extraBtn.getAttribute('data-option-value') || '').trim();
        if (!Number.isFinite(targetIx) || !targetVal) return;

        const currentId = Number(this.querySelector('input[name="id"]')?.value);
        const current = this.#findVariant(currentId) || this.#variants[0];
        /** @type {Record<number, string>} */
        const required = { [targetIx]: targetVal };
        const finishIx = this.#config.finishOptionIndex;
        const sizeIx = this.#config.sizeOptionIndex;

        if (current) {
          required[finishIx] = optionAt(current, finishIx);
          if (typeof sizeIx === 'number') required[sizeIx] = optionAt(current, sizeIx);
          this.#config.extraOptionIndices.forEach((ix) => {
            if (ix !== targetIx) required[ix] = optionAt(current, ix);
          });
        }

        let next = this.#findBestVariant(required, current);
        if (!next && current && typeof sizeIx === 'number') {
          delete required[sizeIx];
          next = this.#findBestVariant(required, current);
        }
        if (!next) {
          const repId = Number(extraBtn.getAttribute('data-representative-variant-id'));
          next = this.#findVariant(repId);
        }
        if (next) this.#applyVariant(next);
      }
    });
  }

  #bindGalleryThumbs() {
    this.querySelectorAll('[data-thumb-button]').forEach((btn) => {
      btn.addEventListener('click', () => this.#activateGalleryThumb(btn));
    });
  }

  #bindGalleryNav() {
    this.querySelectorAll('[data-gallery-step]').forEach((nav) => {
      nav.addEventListener('click', () => {
        const delta = Number(nav.getAttribute('data-gallery-step'));
        const thumbs = [...this.querySelectorAll('[data-thumb-button]')];
        if (!thumbs.length || !Number.isFinite(delta)) return;

        let i = thumbs.findIndex((t) => t.classList.contains('is-active'));
        if (i < 0) i = 0;
        const n = (i + delta + thumbs.length) % thumbs.length;
        this.#activateGalleryThumb(thumbs[n]);
      });
    });
  }

  /** @param {Element} btn */
  #activateGalleryThumb(btn) {
    const id = btn.getAttribute('data-media-id');
    const main = this.querySelector('[data-main-image]');
    const thumbImg = btn.querySelector('img');
    if (!main || main.tagName !== 'IMG' || !id) return;

    const fullSrc = btn.getAttribute('data-full-src');
    const nextSrc = fullSrc || (thumbImg instanceof HTMLImageElement ? thumbImg.currentSrc || thumbImg.src : '');
    if (!nextSrc) return;

    main.src = nextSrc;
    main.removeAttribute('srcset');
    main.sizes = '(min-width: 990px) 45vw, 100vw';
    if (thumbImg instanceof HTMLImageElement && thumbImg.alt) main.alt = thumbImg.alt;

    this.querySelectorAll('[data-thumb-button]').forEach((t) => {
      const on = t.getAttribute('data-media-id') === id;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-current', on ? 'true' : 'false');
    });
  }

  /** @param {object} variant */
  #applyVariant(variant) {
    const idInput = this.querySelector('input[name="id"]');
    if (idInput) idInput.value = String(variant.id);

    this.#dispatchVariantUpdate(variant);
    this.#updateAddToCartLabel(variant);
    this.#updatePriceUi(variant);
    this.#updateGallery(variant);
    this.#updateFinishState(variant);
    this.#updateExtraOptionState(variant);
    this.#updateHeightState(variant);
    this.#updateSizeCards(variant);
    this.#updateProductUrl(variant);
  }

  /** @param {object} variant */
  #updateAddToCartLabel(variant) {
    const label = this.querySelector('.add-to-cart-text__content');
    if (!label) return;
    if (variant.available) {
      label.textContent = this.dataset.textAvailable || label.textContent;
    } else {
      label.textContent = this.dataset.textSoldOut || this.dataset.textUnavailable || label.textContent;
    }
  }

  /** @param {object} variant */
  #dispatchVariantUpdate(variant) {
    const html = document.implementation.createHTMLDocument('');
    const resource = {
      id: variant.id,
      available: variant.available,
      featured_media: variant.featured_media ?? null,
    };

    this.dispatchEvent(
      new VariantUpdateEvent(resource, 'product-configurator', {
        html,
        productId: String(this.#config.productId),
      })
    );
  }

  /** @param {object} variant */
  #updatePriceUi(variant) {
    const summaryPrice = this.querySelector('[data-summary-price]');
    if (summaryPrice) summaryPrice.textContent = variant.price_money || '';

    const summaryLine = this.querySelector('[data-summary-line]');
    const summaryCore = this.querySelector('[data-summary-line-core]');
    const staticHeat = this.querySelector('[data-summary-static-heat]');

    const f = optionAt(variant, this.#config.finishOptionIndex);
    const w = variant.width;
    const h = variant.height;
    const bits = [f];
    if (w != null && h != null) bits.push(`${h}×${w}mm`);

    if (summaryCore) {
      summaryCore.textContent = bits.filter(Boolean).join(' · ');
    } else if (summaryLine) {
      const heat = variant.heat_watts;
      const legacyBits = [...bits];
      if (heat != null) legacyBits.push(`${heat}W`);
      summaryLine.textContent = legacyBits.filter(Boolean).join(' · ');
    }

    if (staticHeat instanceof HTMLElement) {
      const heat = variant.heat_watts;
      staticHeat.textContent = heat != null ? ` · ${heat}W` : '';
    }

    if (typeof window.FlowTemperatureManager?.refreshAll === 'function') {
      window.FlowTemperatureManager.refreshAll();
    }

    const deliveryEl = this.querySelector('[data-summary-delivery]');
    if (deliveryEl) {
      deliveryEl.textContent = variant.delivery
        ? `${variant.delivery} · ${deliveryEl.dataset.suffix || ''}`.trim()
        : deliveryEl.dataset.suffix || '';
    }

    const caption = this.querySelector('[data-gallery-caption-finish]');
    if (caption) {
      caption.textContent = optionAt(variant, this.#config.finishOptionIndex) || '';
    }
  }

  /** @param {object} variant */
  #updateGallery(variant) {
    const fm = variant.featured_media;
    const main = this.querySelector('[data-main-image]');
    if (main && main instanceof HTMLImageElement && fm?.preview_image?.src) {
      main.src = fm.preview_image.src;
      main.removeAttribute('srcset');
      main.sizes = '(min-width: 990px) 45vw, 100vw';
    }

    const mid = fm?.id ? String(fm.id) : null;
    if (mid) {
      this.querySelectorAll('[data-thumb-button]').forEach((t) => {
        const on = t.getAttribute('data-media-id') === mid;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-current', on ? 'true' : 'false');
      });
    }
  }

  /** @param {object} variant */
  #updateFinishState(variant) {
    const f = optionAt(variant, this.#config.finishOptionIndex);
    this.querySelectorAll('[data-finish-button]').forEach((btn) => {
      const on = (btn.getAttribute('data-finish-value') || '').trim() === f;
      btn.classList.toggle('is-selected', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  /** @param {object} variant */
  #updateHeightState(variant) {
    if (!this.#isCastIronLayout()) return;
    const heightIx = this.#config.heightOptionIndex;
    if (typeof heightIx !== 'number') return;
    const height = optionAt(variant, heightIx);
    this.querySelectorAll('[data-height-button]').forEach((btn) => {
      const on = (btn.getAttribute('data-height-value') || '').trim() === height;
      btn.classList.toggle('is-selected', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  /** @param {object} variant */
  #updateExtraOptionState(variant) {
    this.querySelectorAll('[data-extra-option-button]').forEach((btn) => {
      const ix = Number(btn.getAttribute('data-option-index'));
      if (!Number.isFinite(ix)) return;
      const val = (btn.getAttribute('data-option-value') || '').trim();
      const on = optionAt(variant, ix) === val;
      btn.classList.toggle('is-selected', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }

  /** @param {object} variant */
  #updateSizeCards(variant) {
    if (!this.#config.showSizeSelector) return;

    const finish = optionAt(variant, this.#config.finishOptionIndex);
    const castIron = this.#isCastIronLayout();
    const height =
      castIron && typeof this.#config.heightOptionIndex === 'number'
        ? optionAt(variant, this.#config.heightOptionIndex)
        : '';
    const extraIndices = Array.isArray(this.#config.extraOptionIndices)
      ? this.#config.extraOptionIndices
      : [];
    const extraValues =
      !castIron && extraIndices.length > 0
        ? new Map(extraIndices.map((ix) => [ix, optionAt(variant, ix)]))
        : null;

    this.querySelectorAll('[data-size-card]').forEach((card) => {
      const cardFinish = (card.getAttribute('data-finish-value') || '').trim();
      const cardHeight = (card.getAttribute('data-height-value') || '').trim();
      let show = cardFinish === finish && finish !== '';
      if (show && castIron) {
        show = cardHeight === height && height !== '';
      }
      if (show && extraValues?.size) {
        for (const [ix, val] of extraValues.entries()) {
          const cardVal = (card.getAttribute(`data-option-${ix}`) || '').trim();
          if (cardVal !== val) {
            show = false;
            break;
          }
        }
      }

      card.toggleAttribute('hidden', !show);
      card.classList.toggle('rfhp-size-card--concealed', !show);
      card.style.display = show ? '' : 'none';

      const id = Number(card.getAttribute('data-variant-id'));
      const selected = id === Number(variant.id);
      card.classList.toggle('is-selected', selected);
      card.setAttribute('aria-pressed', selected ? 'true' : 'false');

      const label = card.querySelector('[data-card-selected-label]');
      if (label) label.hidden = !selected;

      const priceEl = card.querySelector('[data-card-price]');
      if (selected && priceEl) {
        const v = this.#findVariant(id);
        if (v?.price_money) priceEl.textContent = v.price_money;
      }
    });
  }

  /** @param {number} variantId */
  #syncUiFromVariantId(variantId) {
    let v = this.#findVariant(variantId);
    if (!v && this.#variants.length) v = this.#variants[0];
    if (!v) return;

    this.#updatePriceUi(v);
    this.#updateGallery(v);
    this.#updateFinishState(v);
    this.#updateExtraOptionState(v);
    this.#updateHeightState(v);
    this.#updateSizeCards(v);
  }

  /** @param {object} variant */
  #updateProductUrl(variant) {
    if (!window.location.pathname.includes('/products/')) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', url.toString());
  }
}

if (!customElements.get('product-configurator')) {
  customElements.define('product-configurator', ProductConfigurator);
}
